import { DependencyList, useEffect, useState } from 'react';

export interface AsyncValueState<T> {
  value?: T;
  loading: boolean;
  error?: Error;
}

/**
 * Minimal async loader hook. Keeps the previous value while reloading so
 * periodic refreshes do not make the UI flicker.
 */
export function useAsyncValue<T>(load: () => Promise<T>, deps: DependencyList): AsyncValueState<T> {
  const [state, setState] = useState<AsyncValueState<T>>({ loading: true });

  useEffect(() => {
    let cancelled = false;
    setState(previous => ({ ...previous, loading: true }));
    load().then(
      value => {
        if (!cancelled) setState({ value, loading: false });
      },
      error => {
        if (!cancelled) {
          setState({ error: error instanceof Error ? error : new Error(String(error)), loading: false });
        }
      },
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}

/** Formats an ISO timestamp relative to now, e.g. "3 days ago" or "in 2 hours". */
export function formatRelative(iso: string | undefined, now: number = Date.now()): string {
  const time = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(time)) return '';
  const seconds = Math.round((time - now) / 1000);
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31536000],
    ['month', 2592000],
    ['week', 604800],
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60],
  ];
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  for (const [unit, unitSeconds] of units) {
    if (Math.abs(seconds) >= unitSeconds) {
      return formatter.format(Math.round(seconds / unitSeconds), unit);
    }
  }
  return formatter.format(0, 'minute');
}

/** Formats an ISO timestamp as a short local date, e.g. "Nov 3, 2026". */
export function formatDate(iso: string | undefined): string {
  const time = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(time)) return '';
  return new Date(time).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
